import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import CircleIcon from '@mui/icons-material/Circle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import type { SxProps, Theme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { ReactNode } from 'react';
import * as React from 'react';
import Carousel from 'react-material-ui-carousel';
import type { CarouselProps } from 'react-material-ui-carousel/dist/components/types';
import { baseImgUrl } from '~/utils/constants';
import { MuiRemixLink } from '../shared/links';

export type ShopContentSlideType = {
  eyebrow: string;
  title: string;
  buttonText: string;
  buttonLink: string;
  imageUrl: { desktop: string; mobile: string };
  description: null;
  sx?: SxProps;
};

export default function ShopCarousel(
  props: {
    children: ReactNode | ReactNode[];
    buttonSx?: SxProps<Theme>;
    indicatorContainerSx?: React.CSSProperties;
    indicatorIconButtonSx?: React.CSSProperties;
    indicatorContainerProps?: CarouselProps['indicatorContainerProps'];
    containerSx?: SxProps<Theme>;
    disableButtons?: boolean;
  } & CarouselProps
) {
  React.useEffect(() => {
    document.querySelectorAll<HTMLElement>('.disableButtonWrapperHoverEffects').forEach((element, key) => {
      element.className = '';
      element.style.position = 'absolute';
      element.style[key % 2 === 0 ? 'right' : 'left'] = '0';
      element.style.zIndex = '1';
    });
  }, []);

  const {
    children,
    buttonSx,
    indicatorContainerSx,
    indicatorIconButtonSx,
    indicatorContainerProps,
    containerSx,
    disableButtons,
  } = props;

  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const showButtons = Array.isArray(children) && children.length > 1;

  return (
    <Carousel
      sx={{
        overflow: { xs: 'hidden', md: 'visible' },
        height: { xs: 226, md: 316 },
        ...containerSx,
      }}
      navButtonsAlwaysVisible
      navButtonsAlwaysInvisible={!showButtons}
      fullHeightHover={false}
      navButtonsWrapperProps={{
        className: 'disableButtonWrapperHoverEffects',
        style: { height: '100%', top: '0', display: 'flex', justifyContent: 'center', alignItems: 'center' },
      }}
      NavButton={({ onClick, className, style, next, prev }) => (
        <Button
          disabled={disableButtons}
          className={className}
          style={style}
          onClick={(e) => onClick(e)}
          sx={{
            display: { xs: 'none', md: 'block' },
            borderRadius: '100%',
            width: '56px',
            height: '56px',
            minWidth: 'unset',
            opacity: '1 !important',
            bgcolor: '#F3F9FB !important',
            border: '4px solid white',
            lineHeight: 0,
            '&:hover': {
              color: '#00598B',
              filter: 'brightness(1) !important',
              WebkitFilter: 'brightness(1) !important',
            },
            ...(prev ? { right: '50%' } : { left: '50%' }),
            ...buttonSx,
          }}
        >
          {prev && <ChevronLeft />}
          {next && <ChevronRight />}
        </Button>
      )}
      IndicatorIcon={<CircleIcon sx={{ width: '12px' }} />}
      indicatorContainerProps={{
        style: {
          display: 'flex',
          width: isMobile ? '100%' : 'fit-content',
          position: 'absolute',
          justifyContent: 'center',
          bottom: isMobile ? '12px' : '30px',
          left: isMobile ? '10px' : '80px',
          zIndex: 1,
          ...indicatorContainerSx,
        },
        ...indicatorContainerProps,
      }}
      indicatorIconButtonProps={{
        style: {
          color: 'transparent',
          backgroundColor: 'transparent',
          border: '2px #009DC8 solid',
          padding: 0,
          height: '12px',
          width: '12px',
          marginRight: '10px',
          ...indicatorIconButtonSx,
        },
      }}
      activeIndicatorIconButtonProps={{
        style: { color: '#009DC8', backgroundColor: '#009DC8', height: '12px', width: '12px' },
      }}
      {...props}
    >
      {children}
    </Carousel>
  );
}

export function ShopCarouselSlide({
  eyebrow,
  title,
  buttonText,
  buttonLink,
  imageUrl,
  description,
  sx,
}: ShopContentSlideType) {
  return (
    <Box
      sx={{
        height: { xs: '226px', md: '316px' },
        bgcolor: `#313156`,
        borderRadius: { xs: 0, md: 4 },
        px: { xs: 2, md: '80px' },
        py: { xs: 4, md: '50px' },
        backgroundImage: { xs: `url(${baseImgUrl}${imageUrl.mobile})`, md: `url(${baseImgUrl}${imageUrl.desktop})` },
        backgroundSize: 'cover',
        backgroundPosition: 'right bottom',
        backgroundRepeat: 'no-repeat',
        ...sx,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: description ? { xs: '40%', md: '35%', lg: '55%' } : { xs: '65%', lg: '75%' },
          height: '100%',
        }}
      >
        {eyebrow && (
          <Typography
            display={{ xs: 'none', md: 'block' }}
            variant="h5"
            fontSize={!title ? { xs: 20, md: 24, lg: 34 } : 20}
            fontWeight={500}
            marginBottom={!title ? 3 : 1}
            color="white"
          >
            {eyebrow}
          </Typography>
        )}
        {title && (
          <Typography variant="h2" fontSize={{ xs: 24, sm: 34, lg: 48 }} fontWeight={600} color="white">
            {title}
          </Typography>
        )}
        {description && (
          <Typography fontSize={20} lineHeight="32px" color="white">
            {description}
          </Typography>
        )}
        {buttonLink && buttonText && (
          <MuiRemixLink href={buttonLink} sx={{ textDecoration: 'none', margin: { xs: 'auto 0 0', md: 'auto 0' } }}>
            <Typography
              fontSize={13}
              fontWeight={500}
              letterSpacing="0.46px"
              sx={{
                color: { xs: '#009DC8', md: 'white' },
                textTransform: 'uppercase',
                '&:hover': {
                  color: '#009DC8',
                },
              }}
            >
              {buttonText}
            </Typography>
          </MuiRemixLink>
        )}
      </Box>
    </Box>
  );
}
